import { jwtDecode, type JwtPayload } from 'jwt-decode';
import { GP_ACCESS_TOKEN_COOKIE } from '~/plugins/auth/constants';

export type ParsedToken = JwtPayload & Record<string, unknown>;

export const useOidcStore = defineStore('oidc', () => {
  const state = reactive({
    accessToken: undefined as string | undefined,
    refreshToken: undefined as string | undefined,
    idToken: undefined as string | undefined,
  });

  // gp_at is the canonical access-token source set server-side during the
  // PKCE exchange and refreshed by keycloak-js. We mirror useAccessToken's
  // fallback chain so parsedToken stays populated even when Pinia v3
  // setup-store hydration silently drops state.accessToken on the client
  // (the guard in auth.keycloak.client.ts skips auth.init() in that case
  // to preserve the SSR-set cookie, so onAccessTokenChange never fires
  // and state.accessToken would otherwise stay undefined client-side).
  const accessTokenCookie = useCookie<string | undefined>(
    GP_ACCESS_TOKEN_COOKIE,
  );

  const setTokens = ({
    accessToken,
    refreshToken,
    idToken,
  }: {
    accessToken: string;
    refreshToken: string;
    idToken: string;
  }) => {
    state.accessToken = accessToken;
    state.refreshToken = refreshToken;
    state.idToken = idToken;
  };

  const resetTokens = () => {
    state.accessToken = undefined;
    state.refreshToken = undefined;
    state.idToken = undefined;
  };

  const parsedToken = computed((): ParsedToken => {
    const token = state.accessToken || accessTokenCookie.value || undefined;
    if (!token) return {} as ParsedToken;
    try {
      return jwtDecode<ParsedToken>(token);
    } catch (e) {
      return {} as ParsedToken;
    }
  });

  return {
    ...toRefs(state),
    parsedToken,
    setTokens,
    resetTokens,
  };
});
